import { Button } from "components/DesignSystem/Button/Button";
import { useFormikContext } from "formik";
import { personaAutoFillSalesTax } from "formValidations/autofillSchema";
import { businessDetailsSchema } from "formValidations/businessDetailsSchema";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import {
  useCreateSalesTaxBusinessMutation,
  useGetSalexTaxBusinessQuery,
  useUpdateSalesTaxBusinessMutation,
} from "store/apis/salesTax";
import { EntityDataAttribute } from "types/Models/entity";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { BackendError } from "types/utils/error";

export const SalesTaxOnboardingSubmit = ({
  changeStep,
  showComplete,
}: {
  changeStep: (step: "PREV_STEP" | "NEXT_STEP") => void;
  showComplete?: boolean;
}) => {
  const entityId = useAppSelector((state) => state.onboardingWidget.entityId);
  const { alertToast, successToast } = useToast();
  const { data: business } = useGetSalexTaxBusinessQuery(
    { entityId: entityId! },
    { skip: !entityId }
  );

  const { values } = useFormikContext<{ persona_details: PersonaAutofill }>();

  const [createSalesTaxBusiness] = useCreateSalesTaxBusinessMutation();
  const [updateSalesTaxBusiness] = useUpdateSalesTaxBusinessMutation();
  const { uuid: groupId } = useCurrentGroupContext();

  const { data: currentEntityDetails = [] } = useGetEntityDataAttributesQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    {
      skip: !groupId || !entityId,
    }
  );
  const businessDetailsValues = currentEntityDetails.reduce((acc, field) => {
    acc[field.name] = field.value;
    return acc;
  }, {} as Record<string, EntityDataAttribute["value"]>);

  const onClick = async () => {
    try {
      if (business && showComplete) {
        await updateSalesTaxBusiness({
          entityId: entityId!,
          payload: {
            owner_id: values.persona_details.uuid as string,
          },
        }).unwrap();
      } else if (showComplete) {
        await createSalesTaxBusiness({
          entityId: entityId!,
          payload: {
            owner_id: values.persona_details.uuid as string,
          },
        }).unwrap();
      }

      if (showComplete) {
        successToast({
          message: "Sales tax onboarding completed successfully.",
        });
      }

      return changeStep("NEXT_STEP");
    } catch (error: any) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error
      );
    }
  };

  const validateThenSubmit = async () => {
    try {
      if (showComplete) {
        await personaAutoFillSalesTax.validate(values?.persona_details);
      } else {
        await businessDetailsSchema.validate(businessDetailsValues);
      }

      onClick();
    } catch (error) {
      alertToast({
        message: "Please provide all the details to proceed.",
      });
    }
  };

  return (
    <div className="t-flex t-gap-2">
      <Button onClick={validateThenSubmit}>
        {showComplete ? "Complete onboarding" : "Next"}
      </Button>
    </div>
  );
};
